import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { PantallaLogin } from "../modules/cuenta/PantallaLogin";
import { Esqueleto } from "../ui";
import { inicializarSesion, useAutenticacion } from "./autenticacion";

// Un solo React.lazy() por módulo — este router es genérico y estable,
// ningún WP posterior debería necesitar tocarlo (§ criterios F06).
const PantallaRegistro = lazy(() => import("../modules/cuenta/PantallaRegistro").then((m) => ({ default: m.PantallaRegistro })));
const cargarShell = () => import("./Shell").then((m) => ({ default: m.Shell }));
const Shell = lazy(cargarShell);
const RutasCuenta = lazy(() => import("../modules/cuenta/routes"));
const cargarInicio = () => import("../modules/inicio/routes");
const RutasInicio = lazy(cargarInicio);
const RutasGanado = lazy(() => import("../modules/ganado/routes"));
const RutasRegistro = lazy(() => import("../modules/registro/routes"));
const RutasInsumos = lazy(() => import("../modules/insumos/routes"));
const RutasFinanzas = lazy(() => import("../modules/finanzas/routes"));
const RutasReportes = lazy(() => import("../modules/reportes/routes"));
const RutasImportacion = lazy(() => import("../modules/importacion/routes"));
const RutasConfiguracion = lazy(() => import("../modules/configuracion/routes"));
const GaleriaDevUi = import.meta.env.DEV ? lazy(() => import("./dev-ui/Galeria")) : null;

// Entrada directa a una pantalla autenticada: baja el shell y el módulo en paralelo con
// el refresh de sesión, en lugar de encadenarlos después (mejora FCP/LCP en móvil).
if (
  import.meta.env.MODE !== "test" &&
  typeof window !== "undefined" &&
  !/^\/(login|registro|dev)(\/|$)/.test(window.location.pathname)
) {
  void cargarShell();
  if (/^\/(inicio(\/|$)|$)/.test(window.location.pathname)) void cargarInicio();
}

function CargandoModulo() {
  return (
    <div className="p-4">
      <Esqueleto className="h-6 w-40" />
      <Esqueleto className="mt-3 h-32 w-full" />
    </div>
  );
}

function RutaProtegida({ children }: { children: React.ReactNode }) {
  const { autenticado, inicializando } = useAutenticacion();
  if (inicializando) return <CargandoModulo />;
  if (!autenticado) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function EnrutadorApp() {
  useEffect(() => { void inicializarSesion(); }, []);
  return (
    <Suspense fallback={<CargandoModulo />}>
      <Routes>
        <Route path="/login" element={<PantallaLogin />} />
        <Route path="/registro" element={<PantallaRegistro />} />
        {GaleriaDevUi ? <Route path="/dev/ui" element={<GaleriaDevUi />} /> : null}

        <Route
          element={
            <RutaProtegida>
              <Shell />
            </RutaProtegida>
          }
        >
          <Route index element={<Navigate to="/inicio" replace />} />
          <Route path="inicio/*" element={<RutasInicio />} />
          <Route path="ganado/*" element={<RutasGanado />} />
          <Route path="registro/*" element={<RutasRegistro />} />
          <Route path="insumos/*" element={<RutasInsumos />} />
          <Route path="finanzas/*" element={<RutasFinanzas />} />
          <Route path="reportes/*" element={<RutasReportes />} />
          <Route path="importacion/*" element={<RutasImportacion />} />
          <Route path="configuracion/*" element={<RutasConfiguracion />} />
          <Route path="cuenta/*" element={<RutasCuenta />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
